import { type AnyConditions } from 'json-rules-engine';
import _ from 'lodash';
import {
  convertAstToJsonRulesEngine,
  validateAst,
} from '@bbkAdminComponents/experiences/audiences/edit-rule/edit-rule-context';
import { TriggerEventType } from '@bbkAdminComponents/play/play-trigger';
import { RiskLevel } from '@bbkAdminComponents/predictive/customers-preview';
import {
  PlayPresentation,
  PlayType,
  type PlayEdit,
} from '@bbkAdminRedux/rtkq/play.slice';
import { BrightbackExpression } from '@bbkAdminUtils/brightback-expression';

/*
Validation:
- Audience: audience (rule_id) required.
- Trigger: trigger rules required; PAGE_VISIT must include at least one valid URL rule.
- Targeting: control target when control > 0; an action is required; tests need targets and percentages sum to 100; sub-audience routes need audiences/targets; fallback is required only for cancel page and pricing table.
- Review: runs audience, trigger, targeting; name required.
*/

export type ValidationResult = {
  isValid: boolean;
  errors: string[];
};

export const getTargetLabel = (
  presentation?: PlayPresentation | string
): string => {
  if (presentation === PlayPresentation.PRICING_PAGE) {
    return 'pricing table';
  }
  if (presentation === PlayPresentation.CANCEL_PAGE) {
    return 'cancel page';
  }
  return 'offer';
};

export const getTargetLabelCapitalized = (
  presentation?: PlayPresentation | string
): string => {
  if (presentation === PlayPresentation.PRICING_PAGE) {
    return 'Pricing Table';
  }
  if (presentation === PlayPresentation.CANCEL_PAGE) {
    return 'Cancel Page';
  }
  return 'Offer';
};

export type StepValidationRule = {
  validate: (play: PlayEdit) => ValidationResult;
  errorMessage: (play: PlayEdit) => string;
};

const DUMMY_AUDIENCE_IDS = [RiskLevel.High, RiskLevel.Medium, RiskLevel.Low];

export const isDummyAudienceId = (audienceId: string | undefined): boolean => {
  return DUMMY_AUDIENCE_IDS.includes(audienceId as RiskLevel);
};

export const validateAudienceStep = (play: PlayEdit): ValidationResult => {
  const errors: string[] = [];

  // undefined = targeting all subscribers (valid)
  // empty string or falsy but not undefined = no audience selected yet (invalid)
  if (play?.rule_id !== undefined && !play?.rule_id) {
    errors.push('Please select an audience or enable "Target all subscribers"');
  }

  if (isDummyAudienceId(play?.rule_id)) {
    errors.push('Please confirm the audience before continuing');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const validateTriggerStep = (play: PlayEdit): ValidationResult => {
  const errors: string[] = [];

  const playTrigger = play?.trigger_rules;

  if (!playTrigger || _.isEmpty(playTrigger)) {
    errors.push('Please configure a trigger');
    return { isValid: false, errors };
  }

  const triggerType = playTrigger?.event_type;

  if (triggerType === TriggerEventType.PAGE_VISIT) {
    try {
      const ast = BrightbackExpression.stringToAst(
        playTrigger?.expression as string
      );
      const validatedAst = validateAst(ast);
      const jsonRule = convertAstToJsonRulesEngine(
        validatedAst
      ) as AnyConditions;
      const triggerData =
        (jsonRule.any as { fact: string; value: string; operator: string }[]) ||
        [];

      if (triggerData.length === 0) {
        errors.push('Please add at least one page URL rule');
        return { isValid: false, errors };
      }

      triggerData.forEach((data, index) => {
        // For exact match operators (==, !=, Generic.isBlank), require valid URL
        if (['==', '!=', 'Generic.isBlank'].includes(data.operator)) {
          if (_.isEmpty(data.value)) {
            errors.push(`Page URL rule ${index + 1} must have a value`);
          } else {
            try {
              new URL(data.value);
            } catch (error) {
              errors.push(`Page URL rule ${index + 1} must be a valid URL`);
            }
          }
        }
        // For string operators (contains, does not contain), just require non-empty value
        else if (
          ['String.includes', 'String.notIncludes'].includes(data.operator)
        ) {
          if (_.isEmpty(data.value)) {
            errors.push(`Page URL rule ${index + 1} value is required`);
          }
        }
      });
    } catch (error) {
      errors.push('Invalid trigger expression');
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Utility function to validate a single URL
export const validateUrlFormat = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch (error) {
    return false;
  }
};

// Utility function to validate a single trigger rule value
export const validateTriggerRuleValue = (
  value: string,
  operator: string
): { isValid: boolean; errorMessage?: string } => {
  // For exact match operators (==, !=, Generic.isBlank), require valid URL
  if (['==', '!=', 'Generic.isBlank'].includes(operator)) {
    if (_.isEmpty(value)) {
      return {
        isValid: false,
        errorMessage:
          'Please enter a valid URL (e.g., https://www.example.com)',
      };
    }
    if (!validateUrlFormat(value)) {
      return {
        isValid: false,
        errorMessage:
          'Please enter a valid URL (e.g., https://www.example.com)',
      };
    }
    return { isValid: true };
  }
  // For string operators (contains, does not contain), just require non-empty value
  else if (['String.includes', 'String.notIncludes'].includes(operator)) {
    if (_.isEmpty(value)) {
      return {
        isValid: false,
        errorMessage: 'Value is required',
      };
    }
    return { isValid: true };
  }

  return { isValid: true };
};

export const validateTargetingStep = (play: PlayEdit): ValidationResult => {
  const errors: string[] = [];

  if (!play) {
    errors.push('Play data is missing');
    return { isValid: false, errors };
  }

  const playType = play.play_type as PlayType;
  const presentation = play.presentation as PlayPresentation | undefined;
  const targetLabel = getTargetLabel(presentation);
  const targetLabelCapitalized = getTargetLabelCapitalized(presentation);

  const controlPercent = _.get(
    play,
    'draft_targeting.routing_percents[0].percent',
    0
  );
  const controlTargetKey = _.get(
    play,
    'draft_targeting.routing_percents[0].target.target_key',
    ''
  );

  const treatmentNode = _.get(play, 'draft_targeting.routing_percents[1]');
  const treatmentType = _.get(treatmentNode, 'type') as string;

  switch (playType) {
    case PlayType.RETENTION:
    case PlayType.UPSELL:
    case PlayType.ACQUISITION:
      const isInAppPresentation =
        presentation === PlayPresentation.MODAL ||
        presentation === PlayPresentation.POP_UP ||
        presentation === PlayPresentation.BANNER;

      const isCancelPagePresentation =
        presentation === PlayPresentation.CANCEL_PAGE;

      // Skip control validation for cancel pages
      if (!isCancelPagePresentation) {
        if (controlPercent > 0 && controlTargetKey.length === 0) {
          errors.push(`Control group must have a ${targetLabel} selected`);
        }

        if (
          !isInAppPresentation &&
          controlPercent > 0 &&
          controlTargetKey === 'none'
        ) {
          errors.push(`Control group must have a ${targetLabel} selected`);
        }
      }

      if (!treatmentNode) {
        errors.push(
          `Please select an action (Show ${targetLabelCapitalized}, Test ${targetLabelCapitalized}s, or Sub-audience Split)`
        );
        break;
      }

      if (treatmentType === 'routingToTargetPercent') {
        const treatmentTargetKey = _.get(
          treatmentNode,
          'target.target_key',
          ''
        );
        if (!treatmentTargetKey) {
          errors.push(`Please select a ${targetLabel}`);
        }
      } else if (treatmentType === 'routingToNodePercent') {
        const routingNodeType = _.get(
          treatmentNode,
          'routing_node.type'
        ) as string;

        if (!routingNodeType) {
          errors.push(
            `Please select an action (Show ${targetLabelCapitalized}, Test ${targetLabelCapitalized}s, or Sub-audience Split)`
          );
        } else if (routingNodeType === 'percentRoutingNode') {
          const routingPercents = _.get(
            treatmentNode,
            'routing_node.routing_percents',
            []
          );

          if (routingPercents.length === 0) {
            errors.push(`Please add at least one ${targetLabel} variant`);
          } else {
            let hasInvalidVariant = false;
            routingPercents.forEach((variant: any) => {
              const targetKey = _.get(variant, 'target.target_key');
              if (!targetKey) {
                hasInvalidVariant = true;
              }
            });

            if (hasInvalidVariant) {
              errors.push(
                `All test variants must have a ${targetLabel} selected`
              );
            }

            const totalPercent = routingPercents.reduce(
              (sum: number, variant: any) => {
                return sum + (variant.percent || 0);
              },
              0
            );

            if (totalPercent !== 100) {
              errors.push(
                `Test variant percentages must add up to 100% (currently: ${totalPercent}%)`
              );
            }
          }
        } else if (routingNodeType === 'rulesRoutingNode') {
          const routingRules = _.get(
            treatmentNode,
            'routing_node.routing_rules',
            []
          );
          const fallback = _.get(treatmentNode, 'routing_node.fallback');
          const subAudienceValidation = validateSubAudienceSplit(
            routingRules,
            fallback,
            targetLabel,
            presentation
          );
          if (!subAudienceValidation.isValid) {
            errors.push(...subAudienceValidation.errors);
          }
        }
      } else {
        errors.push(
          `Please select an action (Show ${targetLabelCapitalized}, Test ${targetLabelCapitalized}s, or Sub-audience Split)`
        );
      }
      break;

    default:
      if (!treatmentNode) {
        errors.push('Please select an action');
      }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const validateReviewStep = (
  play: PlayEdit,
  steps?: string[]
): ValidationResult => {
  const errors: string[] = [];

  const audienceValidation = validateAudienceStep(play);
  const targetingValidation = validateTargetingStep(play);

  if (!audienceValidation.isValid) {
    errors.push(...audienceValidation.errors);
  }

  // Only validate trigger if trigger step is present in steps
  const hasTriggerStep = !steps || steps.includes('trigger');
  if (hasTriggerStep) {
    const triggerValidation = validateTriggerStep(play);
    if (!triggerValidation.isValid) {
      errors.push(...triggerValidation.errors);
    }
  }

  if (!targetingValidation.isValid) {
    errors.push(...targetingValidation.errors);
  }

  if (!play?.name || play.name.trim() === '') {
    errors.push('Play name is required');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const validatePlayStep = (
  step: 'audience' | 'trigger' | 'targeting' | 'review',
  play: PlayEdit,
  steps?: string[]
): ValidationResult => {
  switch (step) {
    case 'audience':
      return validateAudienceStep(play);
    case 'trigger':
      return validateTriggerStep(play);
    case 'targeting':
      return validateTargetingStep(play);
    case 'review':
      return validateReviewStep(play, steps);
    default:
      return { isValid: false, errors: ['Unknown step'] };
  }
};

export const usePlayValidation = (
  step: 'audience' | 'trigger' | 'targeting' | 'review',
  play: PlayEdit,
  steps?: string[]
) => {
  return validatePlayStep(step, play, steps);
};

export const validateControlPercentage = (
  percent: number
): ValidationResult => {
  const errors: string[] = [];

  if (percent < 0) {
    errors.push('Control percentage must be at least 0');
  }

  if (percent > 100) {
    errors.push('Control percentage cannot exceed 100');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const validateControlTargetKey = (
  targetKey: string,
  controlPercent: number,
  targetLabel: string = 'offer'
): ValidationResult => {
  const errors: string[] = [];

  if (controlPercent > 0 && !targetKey) {
    errors.push(`Control group must have a ${targetLabel} selected`);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const validatePlayName = (
  name: string,
  existingNames: string[] = []
): ValidationResult => {
  const errors: string[] = [];

  if (!name || name.trim() === '') {
    errors.push('Play name is required');
  }

  if (name && name.length < 1) {
    errors.push('Play name must be at least 1 character');
  }

  if (
    name &&
    existingNames.some(
      (existing) => existing.toLowerCase() === name.toLowerCase()
    )
  ) {
    errors.push(
      'A play with this name already exists. Please choose a different name.'
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const validateABTestVariants = (
  routingPercents: any[],
  targetLabel: string = 'offer'
): ValidationResult => {
  const errors: string[] = [];

  if (routingPercents.length === 0) {
    errors.push(`Please add at least one ${targetLabel} variant`);
    return { isValid: false, errors };
  }

  let hasInvalidVariant = false;
  routingPercents.forEach((variant: any) => {
    const targetKey = _.get(variant, 'target.target_key');
    if (!targetKey) {
      hasInvalidVariant = true;
    }
  });

  if (hasInvalidVariant) {
    errors.push(`All test variants must have a ${targetLabel} selected`);
  }

  const totalPercent = routingPercents.reduce((sum: number, variant: any) => {
    return sum + (variant.percent || 0);
  }, 0);

  if (totalPercent !== 100) {
    errors.push(
      `Test variant percentages must add up to 100% (currently: ${totalPercent}%)`
    );
  }

  if (routingPercents.length < 2) {
    errors.push('A/B test requires at least 2 variants');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const validateSubAudienceSplit = (
  routingRules: any[],
  fallback: any,
  targetLabel: string = 'offer',
  presentation?: PlayPresentation | string
): ValidationResult => {
  const errors: string[] = [];

  const fallbackAllowed =
    presentation === PlayPresentation.CANCEL_PAGE ||
    presentation === PlayPresentation.PRICING_PAGE;

  if (routingRules.length === 0) {
    errors.push('Please add at least one sub-audience rule');
    return { isValid: false, errors };
  }

  routingRules.forEach((rule: any, index: number) => {
    const ruleId = _.get(rule, 'rule_id', '');
    const ruleType = _.get(rule, 'type', '');

    if (!ruleId || (typeof ruleId === 'string' && ruleId.trim() === '')) {
      errors.push(`Sub-audience ${index + 1} must have an audience selected`);
    }

    if (ruleType === 'routingToTargetRule') {
      const targets = _.get(rule, 'targets', []);
      const targetKey =
        targets.length > 0 ? _.get(targets[0], 'target_key', '') : '';

      if (
        !targetKey ||
        (typeof targetKey === 'string' && targetKey.trim() === '')
      ) {
        errors.push(
          `Sub-audience ${index + 1} must have a ${targetLabel} selected`
        );
      }
    } else if (ruleType === 'routingToNodeRule') {
      const routingPercents = _.get(rule, 'routing_node.routing_percents', []);

      if (routingPercents.length === 0) {
        errors.push(
          `Sub-audience ${index + 1} split must have at least one variant`
        );
      } else {
        let hasInvalidVariant = false;
        routingPercents.forEach((variant: any) => {
          const targetKey = _.get(variant, 'target.target_key', '');
          if (
            !targetKey ||
            (typeof targetKey === 'string' && targetKey.trim() === '')
          ) {
            hasInvalidVariant = true;
          }
        });

        if (hasInvalidVariant) {
          errors.push(
            `Sub-audience ${index + 1} split variants must all have a ${targetLabel} selected`
          );
        }

        const totalPercent = routingPercents.reduce(
          (sum: number, variant: any) => {
            return sum + (variant.percent || 0);
          },
          0
        );

        if (totalPercent !== 100) {
          errors.push(
            `Sub-audience ${index + 1} split percentages must add up to 100% (currently: ${totalPercent}%)`
          );
        }
      }
    }
  });

  if (fallbackAllowed) {
    let fallbackTargetKey = '';
    if (fallback?.type === 'routingToTargetRule' && fallback.targets?.[0]) {
      fallbackTargetKey = fallback.targets[0].target_key || '';
    } else {
      fallbackTargetKey = _.get(fallback, 'target_key', '');
    }

    if (
      !fallbackTargetKey ||
      (typeof fallbackTargetKey === 'string' && fallbackTargetKey.trim() === '')
    ) {
      errors.push(`Fallback ${targetLabel} must be selected`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};
